import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/AuthProvider'
import { useOrganization } from '@/hooks/useOrganization'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  LayoutDashboard,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  Plus
} from 'lucide-react'

interface SidebarProps {
  onNewSubmission?: () => void
}

export function Sidebar({ onNewSubmission }: SidebarProps) {
  const location = useLocation()
  const { user, dbUser, signOut } = useAuth()
  const { data: organization } = useOrganization()

  const navItems = [
    {
      label: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      matchTab: 'overview'
    },
    {
      label: 'Submissions',
      href: '/dashboard?tab=submissions',
      icon: FileText,
      matchTab: 'submissions'
    },
    {
      label: 'Settings',
      href: '/settings',
      icon: Settings,
      matchTab: null
    }
  ]

  const isActive = (item: typeof navItems[0]) => {
    // For settings page, just check pathname
    if (item.href === '/settings') {
      return location.pathname === '/settings'
    }
    
    // For dashboard pages, check if we're on dashboard and match the tab
    if (location.pathname === '/dashboard') {
      const currentTab = new URLSearchParams(location.search).get('tab') || 'overview'
      return item.matchTab === currentTab
    }
    
    return false
  }

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="flex h-full w-64 flex-col border-r bg-card">
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 border-b px-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
          <span className="text-sm font-bold text-primary-foreground">L</span>
        </div>
        <span className="text-lg font-bold">LevotLabs</span>
      </div>

      {/* New Submission Button */}
      <div className="p-4">
        <Button onClick={onNewSubmission} className="w-full">
          <Plus className="mr-2 h-4 w-4" />
          New Submission
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3">
        {navItems.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive(item)
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </Link>
        ))}
      </nav>

      {/* User Menu */}
      <div className="border-t p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={dbUser?.avatar_url || undefined} />
                <AvatarFallback>
                  {getInitials(dbUser?.full_name || user?.email)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-1 flex-col items-start text-left">
                <span className="text-sm font-medium">
                  {dbUser?.full_name || user?.email?.split('@')[0]}
                </span>
                <span className="text-xs text-muted-foreground">
                  {organization?.name || 'No organization'}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{dbUser?.full_name}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {user?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
